import React, { useEffect, useState, useMemo } from "react";
import { Modal, Form, Input, Button, message, Spin, Tag, Radio, Empty, Input as AntInput, Tooltip, Tabs, Checkbox, Collapse, InputNumber } from "antd";
import { SearchOutlined, ReloadOutlined, UndoOutlined, ThunderboltOutlined, ExperimentOutlined, RocketOutlined, DownOutlined, UpOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import axios from "axios";
import appConfig from "../../utils/config";

// ─── Constants ───
const DEFAULT_USER_PROMPT = "Generate exactly 5 high-quality SEO-optimized FAQs for the following course.";

const CONTEXT_FIELD_OPTIONS = [
    { label: "Title", value: "title", description: "The course title" },
    { label: "Headline", value: "headlines", description: "Short summary tagline" },
    { label: "Description", value: "description", description: "Full course description" },
    { label: "Outcomes", value: "out_come", description: "What students will learn" },
    { label: "Skills", value: "skills", description: "Skills & competencies taught in the course" },
    { label: "Curriculum", value: "curriculum", description: "Sections & lectures from course contents" },
    { label: "Platform", value: "merchant", description: "The hosting platform (e.g., Udemy, Coursera)" },
    { label: "Level", value: "level", description: "Course difficulty (e.g., Beginner, Advanced)" },
    { label: "Category", value: "category", description: "The course category or niche" },
    { label: "Language", value: "language", description: "The instruction language" },
];

const DEFAULT_CONTEXT_FIELDS = ["title", "headlines", "description"];
const CATEGORY_CONTEXT_DEFAULTS = {
    Skills: ["title", "headlines", "out_come", "curriculum"],
    "Skills Bulk V2": ["title", "headlines", "out_come", "curriculum"],
    "Bulk Level": ["title", "headlines", "description", "curriculum"]
};
const CATEGORY_USER_PROMPT_DEFAULTS = {
    "Skills Bulk V2": "Evaluate each candidate skill for this course.",
    "Bulk Level": "Classify the level of this course based on the provided details."
};
const CATEGORY_INTERNAL_INSTRUCTIONS_DEFAULTS = {
    "Skills Bulk V2": "Return JSON with keys selected and missing. selected must list skillId, skillName, classification, reasoning. classification must be one of SYNONYM, HYPONYM, DISTINCT, UNCERTAIN. missing items: name, optional suggestedParent, reasoning.",
    "Bulk Level": "Return JSON with keys: levelName, reasoning, and confidence (0-1)."
};

const getDefaultContextFields = (category) => CATEGORY_CONTEXT_DEFAULTS[category] || DEFAULT_CONTEXT_FIELDS;
const getDefaultUserPrompt = (category) => CATEGORY_USER_PROMPT_DEFAULTS[category] || DEFAULT_USER_PROMPT;
const getDefaultInternalInstructions = (category, fallback = "") => CATEGORY_INTERNAL_INSTRUCTIONS_DEFAULTS[category] || fallback || "";

// ─── Model Helpers ───
const categorizeModel = (model) => {
    const name = (model.name || "").toLowerCase();
    const desc = (model.description || "").toLowerCase();
    const paramMatch = name.match(/(\d+\.?\d*)b/);
    const paramCount = paramMatch ? parseFloat(paramMatch[1]) : null;

    if (paramCount !== null) {
        if (paramCount >= 30) return { label: "Powerhouse", color: "#722ed1", icon: <RocketOutlined /> };
        if (paramCount >= 7) return { label: "Balanced", color: "#1890ff", icon: <ExperimentOutlined /> };
        return { label: "Fast", color: "#52c41a", icon: <ThunderboltOutlined /> };
    }
    if (desc.includes("large") || desc.includes("advanced")) return { label: "Powerhouse", color: "#722ed1", icon: <RocketOutlined /> };
    if (desc.includes("fast") || desc.includes("small") || desc.includes("efficient")) return { label: "Fast", color: "#52c41a", icon: <ThunderboltOutlined /> };
    return { label: "Balanced", color: "#1890ff", icon: <ExperimentOutlined /> };
};

const getAuthor = (modelName) => {
    if (!modelName) return "";
    const parts = modelName.split("/");
    if (parts.length >= 3) return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
    return "";
};

// ─── Component ───
const AIGeneratorConfig = ({ visible, onClose, defaultPrompt, contextInstructions, category = "FAQ", excludeContextFields = [] }) => {
    const [form] = Form.useForm();

    // Models tab state
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [expandedModels, setExpandedModels] = useState(new Set());

    // Prompts tab state
    const [preservedUserPrompt, setPreservedUserPrompt] = useState(DEFAULT_USER_PROMPT);
    const [contextFields, setContextFields] = useState(DEFAULT_CONTEXT_FIELDS);
    const [fetchedInternalInstructions, setFetchedInternalInstructions] = useState("");
    const [similarityThreshold, setSimilarityThreshold] = useState(0.7);
    const [fuzzyThreshold, setFuzzyThreshold] = useState(0.3);
    const [topK, setTopK] = useState(20);
    const [scoreFloor, setScoreFloor] = useState(0.72);
    const [maxAutoLinksPerCourse, setMaxAutoLinksPerCourse] = useState(8);
    const [confidenceThreshold, setConfidenceThreshold] = useState(0.75);
    const [temperature, setTemperature] = useState(0.1);
    const [maxTokens, setMaxTokens] = useState(2500);

    const { accessToken, user } = useSelector((state) => state.auth);
    const token = accessToken || localStorage.getItem("accessToken");
    
    // Allow prop fallback if it hasn't loaded yet
    const displayInstructions = fetchedInternalInstructions || contextInstructions;

    // ─── Load saved settings when modal opens ───
    useEffect(() => {
        if (visible && token) {
            fetchModels();
            // Fetch from backend API
            axios.get(`${appConfig.apiURL}/v2/admin/ai/config?category=${category}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            }).then(res => {
                const configData = res.data?.results || {};
                setSelectedModel(configData.model || "@cf/meta/llama-3-8b-instruct");
                form.setFieldsValue({
                    prompt: configData.systemPrompt || defaultPrompt || "",
                });
                setPreservedUserPrompt(configData.userPrompt || getDefaultUserPrompt(category));
                setContextFields(configData.contextFields?.length > 0 ? configData.contextFields : getDefaultContextFields(category));
                setFetchedInternalInstructions(configData.internalInstructions || getDefaultInternalInstructions(category, contextInstructions));
                setSimilarityThreshold(configData.similarityThreshold ?? 0.7);
                setFuzzyThreshold(configData.fuzzyThreshold ?? 0.3);
                setTopK(configData.topK ?? 20);
                setScoreFloor(configData.scoreFloor ?? 0.72);
                setMaxAutoLinksPerCourse(configData.maxAutoLinksPerCourse ?? 8);
                setConfidenceThreshold(configData.confidenceThreshold ?? 0.75);
                setTemperature(configData.temperature ?? 0.1);
                setMaxTokens(configData.maxTokens ?? 2500);
            }).catch(err => {
                console.error("Failed to fetch AI configuration", err);
                setSelectedModel("@cf/meta/llama-3-8b-instruct");
                form.setFieldsValue({ prompt: defaultPrompt || "" });
                setPreservedUserPrompt(getDefaultUserPrompt(category));
                setContextFields(getDefaultContextFields(category));
                setFetchedInternalInstructions(getDefaultInternalInstructions(category, contextInstructions));
                setSimilarityThreshold(0.7);
                setFuzzyThreshold(0.3);
                setTopK(20);
                setScoreFloor(0.72);
                setMaxAutoLinksPerCourse(8);
                setConfidenceThreshold(0.75);
                setTemperature(0.1);
                setMaxTokens(2500);
            });
        }
    }, [visible, token, category]);

    // ─── API: Fetch Models ───
    const fetchModels = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await axios.get(`${appConfig.apiURL}/v2/ai/models`, {
                params: { task: "Text Generation", hide_experimental: true, per_page: 100 },
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            if (data.status && data.results?.result) setModels(data.results.result);
            else setFetchError(data.message || "Failed to fetch models");
        } catch (err) {
            console.error("Failed to fetch AI models:", err);
            setFetchError(err.response?.data?.message || "Could not connect to the server. Ensure Cloudflare credentials are configured in Plugins.");
        } finally {
            setLoading(false);
        }
    };

    // ─── Processed & Filtered Models ───
    const processedModels = useMemo(() => models.map(m => ({
        ...m, category: categorizeModel(m), author: getAuthor(m.name)
    })), [models]);

    const filteredModels = useMemo(() => {
        let result = processedModels;
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(m => (m.name || "").toLowerCase().includes(lower) || (m.description || "").toLowerCase().includes(lower));
        }
        if (categoryFilter !== "all") result = result.filter(m => m.category.label.toLowerCase() === categoryFilter);
        return result;
    }, [processedModels, searchTerm, categoryFilter]);

    // ─── Handlers ───
    const handleSave = async () => {
        const promptValue = form.getFieldValue("prompt");
        if (!selectedModel) { message.warning("Please select a model before saving."); return; }
        if (contextFields.length === 0) { message.warning("Please select at least one context field."); return; }
        
        try {
            const payload = {
                category,
                model: selectedModel,
                systemPrompt: promptValue || "",
                userPrompt: preservedUserPrompt,
                contextFields,
                internalInstructions: fetchedInternalInstructions
            };

            if (category === "Skills") {
                payload.similarityThreshold = similarityThreshold;
                payload.fuzzyThreshold = fuzzyThreshold;
            }
            if (category === "Skills Bulk V2") {
                payload.topK = topK;
                payload.scoreFloor = scoreFloor;
                payload.maxAutoLinksPerCourse = maxAutoLinksPerCourse;
                payload.confidenceThreshold = confidenceThreshold;
                payload.temperature = temperature;
                payload.maxTokens = maxTokens;
            }

            const response = await axios.put(`${appConfig.apiURL}/v2/admin/ai/config`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data?.status === false) {
                throw new Error(response.data.message || "Backend rejected the save operation.");
            }

            message.success("AI Configuration saved completely.");
            onClose();
        } catch (error) {
            console.error("Save AI Config Error:", error);
            const detail = error.response?.data?.message || error.message || "Unknown error";
            message.error(`Failed to save AI configuration: ${detail}`);
        }
    };

    const handleResetPrompt = () => {
        form.setFieldsValue({ prompt: defaultPrompt || "" });
        setContextFields(getDefaultContextFields(category));
        if (category === "Skills") {
            setSimilarityThreshold(0.7);
            setFuzzyThreshold(0.3);
        }
        if (category === "Skills Bulk V2" || category === "Bulk Level") {
            setPreservedUserPrompt(getDefaultUserPrompt(category));
            setFetchedInternalInstructions(getDefaultInternalInstructions(category, contextInstructions));
        }
        if (category === "Skills Bulk V2") {
            setTopK(20);
            setScoreFloor(0.72);
            setMaxAutoLinksPerCourse(8);
            setConfidenceThreshold(0.75);
            setTemperature(0.1);
            setMaxTokens(2500);
        }
        message.info("System prompt and context fields reset to defaults.");
    };

    const toggleExpand = (e, modelName) => {
        e.stopPropagation();
        setExpandedModels(prev => {
            const next = new Set(prev);
            if (next.has(modelName)) next.delete(modelName); else next.add(modelName);
            return next;
        });
    };

    // ─── Styles ───
    const cardStyle = (isSelected) => ({
        padding: "12px 16px", borderRadius: 10,
        border: isSelected ? "2px solid #722ed1" : "1px solid #e8e8e8",
        background: isSelected ? "linear-gradient(135deg, rgba(114,46,209,0.06) 0%, rgba(235,47,150,0.06) 100%)" : "#fff",
        cursor: "pointer", transition: "all 0.2s ease",
        boxShadow: isSelected ? "0 2px 12px rgba(114,46,209,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
        marginBottom: 8,
    });

    const helperStyle = { fontSize: 11, color: "#999", marginBottom: 8, lineHeight: "1.5" };

    // ─── Tab 1: Models ───
    const modelsTab = (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>Select Model</span>
                <Tooltip title="Refresh model list">
                    <Button icon={<ReloadOutlined spin={loading} />} size="small" onClick={fetchModels} disabled={loading} style={{ borderRadius: 6 }} />
                </Tooltip>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <AntInput
                    placeholder="Search models..."
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    allowClear style={{ flex: 1, borderRadius: 8 }}
                />
                <Radio.Group value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} size="small" buttonStyle="solid">
                    <Radio.Button value="all" style={{ borderRadius: "6px 0 0 6px" }}>All</Radio.Button>
                    <Radio.Button value="fast"><ThunderboltOutlined /> Fast</Radio.Button>
                    <Radio.Button value="balanced"><ExperimentOutlined /> Balanced</Radio.Button>
                    <Radio.Button value="powerhouse" style={{ borderRadius: "0 6px 6px 0" }}><RocketOutlined /> Power</Radio.Button>
                </Radio.Group>
            </div>

            <div style={{ maxHeight: 320, overflowY: "auto", borderRadius: 10, border: "1px solid #f0f0f0", padding: 8, background: "#fafafa" }}>
                {loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#888" }}>Fetching models from Cloudflare...</div>
                    </div>
                ) : fetchError ? (
                    <div style={{ textAlign: "center", padding: 30 }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: "#ff4d4f" }}>{fetchError}</span>} />
                        <Button type="link" onClick={fetchModels} icon={<ReloadOutlined />}>Retry</Button>
                    </div>
                ) : filteredModels.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No models match your search." />
                ) : (
                    filteredModels.map((model) => (
                        <div
                            key={model.name}
                            style={cardStyle(selectedModel === model.name)}
                            onClick={() => setSelectedModel(model.name)}
                            onMouseEnter={e => { if (selectedModel !== model.name) e.currentTarget.style.borderColor = "#d9d9d9"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
                            onMouseLeave={e => { if (selectedModel !== model.name) e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a1a" }}>
                                            {(model.name || "").replace(/^@cf\//, "").replace(/^@hf\//, "")}
                                        </div>
                                        {model.description && model.description.length > 60 && (
                                            <span
                                                onClick={(e) => toggleExpand(e, model.name)}
                                                style={{ fontSize: 7, color: "#aaa", cursor: "pointer", display: "flex", alignItems: "center", marginLeft: 2, transition: "color 0.2s" }}
                                                onMouseEnter={e => e.currentTarget.style.color = "#722ed1"}
                                                onMouseLeave={e => e.currentTarget.style.color = "#aaa"}
                                            >
                                                {expandedModels.has(model.name) ? <UpOutlined /> : <DownOutlined />}
                                            </span>
                                        )}
                                    </div>
                                    {model.description && (
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{
                                                fontSize: 11, color: "#888",
                                                whiteSpace: expandedModels.has(model.name) ? "normal" : "nowrap",
                                                overflow: "hidden",
                                                textOverflow: expandedModels.has(model.name) ? "clip" : "ellipsis",
                                                maxWidth: 380, lineHeight: "1.4"
                                            }}>
                                                {model.description}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                                    {model.author && <Tag style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>{model.author}</Tag>}
                                    <Tag color={model.category.color} style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>
                                        {model.category.icon} {model.category.label}
                                    </Tag>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {!loading && !fetchError && (
                <div style={{ textAlign: "right", marginTop: 4, fontSize: 11, color: "#aaa" }}>
                    {filteredModels.length} model{filteredModels.length !== 1 ? "s" : ""} available
                </div>
            )}
        </div>
    );

    // ─── Tab 2: Prompts ───
    const promptsTab = (
        <div>
            {/* Context Fields Selector */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>Context Fields</span>
                    <Tooltip title="Reset system prompt and context fields to defaults">
                        <Button type="text" size="small" icon={<UndoOutlined />} onClick={handleResetPrompt} style={{ color: "#888", fontSize: 12 }}>
                            Reset All
                        </Button>
                    </Tooltip>
                </div>
                <div style={helperStyle}>
                    Select which course fields to include as context for the AI. More context generally means better quality inference but increases processing time.
                </div>
                <div style={{
                    background: "#fafafa", borderRadius: 8, border: "1px solid #f0f0f0",
                    padding: "12px 16px"
                }}>
                    <Checkbox.Group
                        value={contextFields}
                        onChange={setContextFields}
                        style={{ width: "100%" }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {CONTEXT_FIELD_OPTIONS
                                .filter(opt => !excludeContextFields.includes(opt.value))
                                .map(opt => (
                                    <Checkbox key={opt.value} value={opt.value}>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>{opt.label}</span>
                                        <span style={{ color: "#999", fontSize: 11, marginLeft: 8 }}>— {opt.description}</span>
                                    </Checkbox>
                                ))
                            }
                        </div>
                    </Checkbox.Group>
                </div>
                {contextFields.length === 0 && (
                    <div style={{ color: "#ff4d4f", fontSize: 11, marginTop: 6 }}>
                        ⚠ Please select at least one context field for the AI to use.
                    </div>
                )}
            </div>

            {category === "Skills" && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 4 }}>
                        Deduplication Thresholds
                    </div>
                    <div style={helperStyle}>
                        Backend uses these values while reviewing generated skills. Lower semantic threshold shows more possible matches; tighten it as your taxonomy matures.
                    </div>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                        background: "#fafafa",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                        padding: "12px 16px"
                    }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                                Semantic similarity
                            </div>
                            <InputNumber
                                min={0}
                                max={1}
                                step={0.01}
                                precision={2}
                                value={similarityThreshold}
                                onChange={(value) => setSimilarityThreshold(value ?? 0.7)}
                                style={{ width: "100%" }}
                            />
                            <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                Cloudflare Vectorize cosine score cutoff.
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                                Fuzzy strictness
                            </div>
                            <InputNumber
                                min={0}
                                max={1}
                                step={0.01}
                                precision={2}
                                value={fuzzyThreshold}
                                onChange={(value) => setFuzzyThreshold(value ?? 0.3)}
                                style={{ width: "100%" }}
                            />
                            <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                Fuse.js score cutoff; lower is stricter.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {category === "Skills Bulk V2" && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 4 }}>
                        Bulk V2 Retrieval & Classification
                    </div>
                    <div style={helperStyle}>
                        These values are saved as defaults for new Skills Bulk V2 jobs. The create screen can still override candidate limits per run.
                    </div>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                        background: "#fafafa",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                        padding: "12px 16px"
                    }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Top K candidates</div>
                            <InputNumber min={1} max={100} value={topK} onChange={(value) => setTopK(value || 20)} style={{ width: "100%" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Score floor</div>
                            <InputNumber min={0} max={1} step={0.01} precision={2} value={scoreFloor} onChange={(value) => setScoreFloor(value ?? 0.72)} style={{ width: "100%" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Max auto-links/course</div>
                            <InputNumber min={1} max={50} value={maxAutoLinksPerCourse} onChange={(value) => setMaxAutoLinksPerCourse(value || 8)} style={{ width: "100%" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Confidence threshold</div>
                            <InputNumber min={0} max={1} step={0.01} precision={2} value={confidenceThreshold} onChange={(value) => setConfidenceThreshold(value ?? 0.75)} style={{ width: "100%" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Temperature</div>
                            <InputNumber min={0} max={1.5} step={0.05} precision={2} value={temperature} onChange={(value) => setTemperature(value ?? 0.1)} style={{ width: "100%" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>Max output tokens</div>
                            <InputNumber min={64} max={8192} value={maxTokens} onChange={(value) => setMaxTokens(value || 2500)} style={{ width: "100%" }} />
                        </div>
                    </div>
                </div>
            )}

            {/* System Prompt */}
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>System Prompt</span>
                </div>
                <div style={helperStyle}>
                    Sets the AI's personality and behavior. This shapes <b>how</b> the AI responds.
                </div>
                <Form form={form} layout="vertical">
                    <Form.Item name="prompt" style={{ marginBottom: 0 }}>
                        <Input.TextArea
                            rows={3}
                            placeholder="e.g. You are a helpful assistant writing course FAQs..."
                            style={{
                                fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                                fontSize: 12, borderRadius: 8, background: "#fafafa"
                            }}
                        />
                    </Form.Item>
                </Form>

                {(category === "Skills Bulk V2" || category === "Bulk Level") && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 4 }}>
                            User Prompt
                        </div>
                        <div style={helperStyle}>
                            Runtime course context and candidate skills are appended by the backend.
                        </div>
                        <Input.TextArea
                            rows={2}
                            value={preservedUserPrompt}
                            onChange={(event) => setPreservedUserPrompt(event.target.value)}
                            style={{
                                fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                                fontSize: 12, borderRadius: 8, background: "#fafafa"
                            }}
                        />

                        <div style={{ fontWeight: 600, fontSize: 14, color: "#333", margin: "12px 0 4px" }}>
                            Internal Instructions
                        </div>
                        <div style={helperStyle}>
                            Keep this as the strict JSON contract used by the bulk classifier.
                        </div>
                        <Input.TextArea
                            rows={4}
                            value={fetchedInternalInstructions}
                            onChange={(event) => setFetchedInternalInstructions(event.target.value)}
                            style={{
                                fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                                fontSize: 12, borderRadius: 8, background: "#fafafa"
                            }}
                        />
                    </div>
                )}

                {/* Internal Instructions Preview */}
                {displayInstructions && category !== "Skills Bulk V2" && (
                    <Collapse 
                        ghost
                        size="small"
                        style={{ marginTop: 8 }}
                        items={[{
                            key: "1",
                            label: (
                                <span style={{ fontSize: 11, color: "#999" }}>
                                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                                    Internal formatting instructions (auto-appended, read-only)
                                </span>
                            ),
                            children: (
                                <div style={{
                                    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                                    fontSize: 11, color: "#666", background: "#f5f5f5",
                                    padding: 10, borderRadius: 6, whiteSpace: "pre-wrap", lineHeight: "1.5"
                                }}>
                                    {displayInstructions}
                                </div>
                            )
                        }]}
                    />
                )}
            </div>
        </div>
    );

    // ─── Render ───
    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: "linear-gradient(135deg, #722ed1, #eb2f96)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 16
                    }}>
                        <RocketOutlined />
                    </div>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>AI Generator Settings</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            width={680}
            styles={{ body: { padding: "0 24px 16px" } }}
        >
            <Tabs
                defaultActiveKey="models"
                items={[
                    {
                        key: "models",
                        label: <span><RocketOutlined style={{ marginRight: 6 }} />Models</span>,
                        children: modelsTab
                    },
                    {
                        key: "prompts",
                        label: <span><EditOutlined style={{ marginRight: 6 }} />Prompts</span>,
                        children: promptsTab,
                        forceRender: true
                    }
                ]}
            />

            {/* ── Footer Actions ── */}
            <div style={{
                display: "flex", justifyContent: "flex-end", gap: 8,
                paddingTop: 12, borderTop: "1px solid #f0f0f0"
            }}>
                <Button onClick={onClose} style={{ borderRadius: 6 }}>Cancel</Button>
                <Button
                    type="primary"
                    onClick={handleSave}
                    style={{
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #722ed1, #eb2f96)",
                        borderColor: "transparent",
                        fontWeight: 500
                    }}
                >
                    Save Settings
                </Button>
            </div>
        </Modal>
    );
};

export default AIGeneratorConfig;
